import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Building2, Eye, EyeOff, ArrowLeft } from "lucide-react";

export const NEON = "#E3FF00";

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md">
        <Link to="/" data-testid="back-to-home-button" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors mb-6">
          <ArrowLeft className="h-4 w-4" /> Torna alla home
        </Link>
        <div className="flex items-center gap-3 sm:gap-4 mb-10 justify-center">
          <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl grid place-items-center font-display font-extrabold text-2xl sm:text-3xl text-neutral-900 shrink-0" style={{ backgroundColor: NEON }}>MP</div>
          <span className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Mypasspartu</span>
        </div>
        <div className="bg-card border-2 border-[#E3FF00] rounded-2xl shadow-sm p-7 sm:p-8">
          <h1 className="font-display text-2xl font-bold tracking-tight text-center">{title}</h1>
          {subtitle && <p className="text-muted-foreground mt-1.5 text-sm text-center">{subtitle}</p>}
          <div className="mt-7">{children}</div>
        </div>
      </div>
    </div>
  );
}

export const field = "w-full bg-background border border-input rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all";
export const label = "text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block";
export const primaryBtn = "w-full bg-[#E3FF00] text-neutral-900 font-semibold px-4 py-2.5 rounded-lg shadow-sm hover:brightness-95 active:scale-[0.98] transition-all disabled:opacity-50";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
      navigate("/home");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Credenziali non valide");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Bentornato" subtitle="Accedi al tuo spazio di gestione">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={label}>Email</label>
          <input data-testid="login-email-input" className={field} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="nome@azienda.it" />
        </div>
        <div>
          <label className={label}>Password</label>
          <div className="relative">
            <input data-testid="login-password-input" className={field + " pr-11"} type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required />
            <button type="button" onClick={() => setShowPw(!showPw)} data-testid="toggle-password-visibility" aria-label={showPw ? "Nascondi password" : "Mostra password"} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="text-right">
          <Link to="/forgot-password" className="text-sm text-primary hover:underline">Password dimenticata?</Link>
        </div>
        <button data-testid="login-submit-button" className={primaryBtn} disabled={loading}>{loading ? "Accesso..." : "Accedi"}</button>
      </form>
      <div className="relative my-5">
        <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
        <div className="relative flex justify-center"><span className="bg-card px-3 text-xs text-muted-foreground">oppure</span></div>
      </div>
      <button type="button" data-testid="google-login-button" onClick={() => {
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        const redirectUrl = window.location.origin + "/home";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      }} className="w-full flex items-center justify-center gap-2.5 bg-[#E3FF00] text-neutral-900 font-semibold px-4 py-2.5 rounded-lg shadow-sm hover:brightness-95 active:scale-[0.98] transition-all">
        <svg className="h-5 w-5" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
        Accedi con Google
      </button>
      <Link to="/register" data-testid="register-link-button" className="mt-4 w-full flex items-center justify-center gap-1.5 bg-[#E3FF00] text-neutral-900 font-semibold px-4 py-2.5 rounded-lg shadow-sm hover:brightness-95 active:scale-[0.98] transition-all">
        Non hai un account? Registrati
      </Link>
      <div className="mt-6 p-3 rounded-lg bg-muted/50 border border-border text-xs text-muted-foreground flex items-start gap-2">
        <Building2 className="h-4 w-4 mt-0.5 shrink-0" />
        <span>Demo: <strong>proprietario@demo.mypasspartu.it</strong> / <strong>Demo2026!</strong></span>
      </div>
    </AuthShell>
  );
}
